<template>
	<div class="flex flex-col items-center group">
		<div class="w-full">
			<span
				v-if="leaflet.validFrom && leafletValidityStartDate(leaflet.validFrom)"
				class="text-center mb-2 typo-body-m block py-1 px-2 rounded-full text-body-secondary bg-brand-mujglobus-green-accent"
			>
				{{ leafletValidityStartDate(leaflet.validFrom) }}
			</span>
			<span
				v-else-if="leaflet.validTo && leafletValidityEndDate(leaflet.validTo)"
				class="text-center mb-2 typo-body-m block text-body-primary py-1 px-2"
			>
				Platný do {{ formatDate(leaflet.validTo) }}
			</span>
			<div class="px-4 pt-4 rounded-lg border border-border-tertiary shadow-md hover:shadow-lg transition-shadow active:shadow-none">
				<NuxtLink
					:to="leafletDetailLocation"
					class="grid bg-white rounded-sm h-0 relative pb-[134.7826%]"
					@click="trackLeafletSelect"
				>
					<NuxtImg
						v-if="getLeafletImageUrl"
						class="w-full h-full object-contain rounded-sm absolute inset-0"
						:src="getLeafletImageUrl"
						alt="Akční leták"
						loading="lazy"
					/>
				</NuxtLink>
				<div class="py-2 mt-4 flex justify-center sm:justify-between items-center gap-2 sm:w-full sm:mt-4 group-hover/link:bg-background-important">
					<NuxtLink
						:to="leafletDetailLocation"
						class="btn btn-tertiary p-0 sm:py-3 !px-0 flex items-center gap-1.5 font-bold typo-body-m"
						:aria-label="getAriaLabel()"
						@click="trackLeafletSelect"
					>
						<NuxtIcon v-if="isDesktop" name="custom/double-chevron" class="text-xs" aria-hidden="true" filled />
						<NuxtIcon
							v-else
							name="custom/arrow-right"
							class="text-xs"
							aria-hidden="true"
							filled
						/>
						Prohlédnout
					</NuxtLink>
					<a
						v-if="leaflet.pdfAsset && isDesktop"
						:href="leaflet.pdfAsset"
						target="_blank"
						class="text-icon-active"
						aria-label="Stáhnout leták"
						download
						@click="trackLeafletDownload"
					>
						<NuxtIcon name="download" filled class="text-lg" />
					</a>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {useGtm} from '@gtm-support/vue-gtm';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import type {Gsoa_ActionOffers_Document as Leaflet} from '~/openapi';
	import {
		getMainLeafletRouteName,
		MAIN_LEAFLET_CURRENT_ROUTE_NAME,
		MAIN_LEAFLET_FUTURE_ROUTE_NAME,
	} from '~/utils/leaflets';
	import {formatPragueDate} from '~/utils/date';

	interface Props {
		section: string;
		counter: number;
		leaflet: Leaflet;
		currentMainLeafletId?: string | null;
		disabledImageLazyLoading?: boolean;
	}

	const props = defineProps<Props>();

	const gtm = useGtm();
	const router = useRouter();
	const route = useRoute();

	const breakpoints = useTailwindBreakpoints();
	const isDesktop = breakpoints.greaterOrEqual('sm');

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarketWithFallbackInstance} = storeToRefs(hypermarketsStore);
	const {visitedHypermarketInstance} = useVisitedHypermarket();
	const branchSpecificPath = useBranchSpecificPath();
	const currentMainLeafletLocation = branchSpecificPath({name: MAIN_LEAFLET_CURRENT_ROUTE_NAME});
	const futureMainLeafletLocation = branchSpecificPath({name: MAIN_LEAFLET_FUTURE_ROUTE_NAME});
	const slugLeafletLocation = computed(() => branchSpecificPath({name: 'letaky-slug', params: {slug: props.leaflet.slug}}).value);

	const formatDate = (date: string) => formatPragueDate(date, 'd.M.yyyy');

	const getLeafletImageUrl = computed(() => {
		const url = props.leaflet.jpgAssets?.[0];
		if (!url) {
			return null;
		}
		return url.includes('storyblok.com') ? url : `${url}&type=2`;
	});

	const leafletDetailLocation = computed(() => {
		switch (getMainLeafletRouteName(props.leaflet, props.currentMainLeafletId)) {
			case MAIN_LEAFLET_CURRENT_ROUTE_NAME:
				return currentMainLeafletLocation.value;
			case MAIN_LEAFLET_FUTURE_ROUTE_NAME:
				return futureMainLeafletLocation.value;
			default:
				return slugLeafletLocation.value;
		}
	});

	const leafletDetailUrl = computed(() => router.resolve(leafletDetailLocation.value));

	const pageType = computed(() => {
		switch (route.name) {
			case 'branch-letaky':
				return 'leaflets_list';
			case 'branch-letaky-slug':
				return 'leaflet_actual';
			default:
				return 'homepage';
		}
	});

	function trackLeafletDownload() {
		gtm?.push({
			event: 'leaflet_download',
			name: props.leaflet?.slug,
			number: props.leaflet?.actionOfferId,
			selected_hypermarket: selectedHypermarketWithFallbackInstance.value?.content.name,
			content_hypermarket: visitedHypermarketInstance.value?.content.name,
			page_type: getLeafletGTMPageType(props.leaflet),
		});
	}

	function trackLeafletSelect() {
		gtm?.push({
			event: 'select_leaflet',
			section: props.section,
			text: `Platný do ${useDateFormat(props.leaflet.validTo!, 'D. M. YYYY').value}`,
			type: selectedHypermarketWithFallbackInstance.value?.content.name,
			target: `${window.location.origin}${leafletDetailUrl.value.fullPath}`,
			counter: String(props.counter),
			page_type: pageType.value,
			selected_hypermarket: selectedHypermarketWithFallbackInstance.value?.content.name,
			content_hypermarket: visitedHypermarketInstance.value?.content.name,
			parameter: props.leaflet.slug,
			name: props.leaflet.actionOfferName,
			id_letaku: props.leaflet.actionOfferId,
		});
	}

	function getAriaLabel() {
		const startDateText = props.leaflet.validFrom ? leafletValidityStartDate(props.leaflet.validFrom) : null;
		const validity = startDateText?.toLowerCase()
			?? (props.leaflet.validTo ? `platný do ${formatDate(props.leaflet.validTo)}` : '');
		return `Prohlédnout leták - ${selectedHypermarketWithFallbackInstance.value?.content.name} ${validity}`;
	}
</script>
